import { produce } from 'immer';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { customEvents } from '../../../../../../common/constants';
import useMount from '../../../../../../common/hooks/useMount';
import Icons from '../../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { CartContext } from '../../../../contexts/CartContext';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { CartItem } from '../../../../models/Cart';
import { ShowCartDropdownEventData } from '../../../../models/CustomEvents';
import { resources } from '../../../../resources/resources';
import { cartService } from '../../../../services/cart';
import CartDropdown from '../CartDropdown';

interface IHelpProps {
  isTablet: boolean;
}

export default function Cart(props: Readonly<IHelpProps>) {
  const { isTablet } = props;
  const { t: translate } = useTranslation();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const { cartRef, showCart } = useContext(CartContext);
  const shouldLoadFromApiRef = useRef<boolean>(true);

  const loadCartItems = async () => {
    setIsLoading(true);
    shouldLoadFromApiRef.current = false; // handleCartOpen is called twice by the popover component, this is to stop the second call
    const r = await cartService.getCartItems();
    shouldLoadFromApiRef.current = true;
    setIsLoading(false);

    if (!r.hasErrors) {
      setCartItems(r.data);
      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.cartItemsCount = r.data.length;
        }),
      );
    } else {
      setHasError(true);
    }
  };

  const handleCartOpen = () => {
    if (shouldLoadFromApiRef.current) {
      loadCartItems();
    }
  };

  const handleCartClose = () => {
    shouldLoadFromApiRef.current = true;
  };

  useMount(() => {
    const handleShowCartDropdown = (e: Event) => {
      const event = e as CustomEvent<ShowCartDropdownEventData>;

      setCartItems(event.detail.cartItems);
      shouldLoadFromApiRef.current = false;

      // the showCart function is delayed because setState is async
      setTimeout(() => showCart(), 0);
    };

    document.addEventListener(
      customEvents.showCartDropdown,
      handleShowCartDropdown,
    );

    return () =>
      document.removeEventListener(
        customEvents.showCartDropdown,
        handleShowCartDropdown,
      );
  });

  const cartContent = (
    <CartDropdown
      cartItems={cartItems}
      isLoading={isLoading}
      hasError={hasError}
    />
  );

  if (isTablet) {
    return (
      <PopoverDialog
        className="dropdown-menu-cart"
        placement="bottom"
        closeFromContent
        type="hover"
        trigger="manual"
        externalRef={cartRef}
        onShow={() => handleCartOpen()}
        onHide={() => handleCartClose()}
        content={cartContent}
      >
        <Link
          to="/cart"
          className="btn app-bar-tools-btn app-bar-cart-m"
          aria-label={translate(resources.menu.cart)}
          onClick={() => cartRef.current?.hide()}
        >
          <span className="btn-icon cart-count">
            <Icons.ShoppingCart />
            {customerInfo.cartItemsCount > 0 && (
              <span>{customerInfo.cartItemsCount}</span>
            )}
          </span>
        </Link>
      </PopoverDialog>
    );
  }

  return (
    <PopoverDialog
      className="dropdown-menu-cart"
      placement="bottom"
      closeFromContent
      type="hover"
      externalRef={cartRef}
      onShow={() => handleCartOpen()}
      onHide={() => handleCartClose()}
      content={cartContent}
    >
      <Link
        to="/cart"
        className="btn app-bar-tools-btn app-bar-cart"
        onMouseEnter={() => handleCartOpen()}
        onMouseLeave={() => handleCartClose()}
      >
        <span className="btn-icon cart-count">
          <Icons.ShoppingCart />
          {customerInfo.cartItemsCount > 0 && (
            <span>{customerInfo.cartItemsCount}</span>
          )}
        </span>
        <span className="btn-text">{translate(resources.menu.cart)}</span>
      </Link>
    </PopoverDialog>
  );
}
